import { useRef, useCallback } from "react";

export function useAudio() {
  const ctxRef = useRef<AudioContext | null>(null);

  const ensureContext = useCallback(() => {
    if (!ctxRef.current) {
      ctxRef.current = new (window.AudioContext || (window as any).webkitAudioContext)();
    }
    if (ctxRef.current.state === "suspended") {
      ctxRef.current.resume();
    }
    return ctxRef.current;
  }, []);

  // Keep as no-op for backward compat
  const unlockAudio = useCallback(() => {
    try { ensureContext(); } catch { /* optional */ }
  }, [ensureContext]);

  const playTone = useCallback((kind: string = "tap") => {
    try {
      const ctx = ensureContext();
      const now = ctx.currentTime;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);

      if (kind === "whoosh") {
        osc.type = "triangle";
        osc.frequency.setValueAtTime(420, now);
        osc.frequency.exponentialRampToValueAtTime(120, now + 0.18);
        gain.gain.setValueAtTime(0.12, now);
        gain.gain.exponentialRampToValueAtTime(0.001, now + 0.20);
        osc.start(now);
        osc.stop(now + 0.22);
      } else if (kind === "hover") {
        osc.type = "sine";
        osc.frequency.setValueAtTime(1600, now);
        gain.gain.setValueAtTime(0.06, now);
        gain.gain.exponentialRampToValueAtTime(0.001, now + 0.05);
        osc.start(now);
        osc.stop(now + 0.06);
      } else if (kind === "fanfare") {
        osc.type = "sine";
        osc.frequency.setValueAtTime(523, now);
        osc.frequency.setValueAtTime(659, now + 0.12);
        osc.frequency.setValueAtTime(784, now + 0.24);
        gain.gain.setValueAtTime(0.15, now);
        gain.gain.exponentialRampToValueAtTime(0.001, now + 0.5);
        osc.start(now);
        osc.stop(now + 0.52);
      } else if (kind === "complete") {
        osc.type = "sine";
        osc.frequency.setValueAtTime(440, now);
        osc.frequency.setValueAtTime(554, now + 0.18);
        osc.frequency.setValueAtTime(659, now + 0.36);
        osc.frequency.setValueAtTime(880, now + 0.54);
        gain.gain.setValueAtTime(0.001, now);
        gain.gain.linearRampToValueAtTime(0.12, now + 0.06);
        gain.gain.linearRampToValueAtTime(0.14, now + 0.3);
        gain.gain.exponentialRampToValueAtTime(0.001, now + 1.1);
        osc.start(now);
        osc.stop(now + 1.15);

        const osc2 = ctx.createOscillator();
        const gain2 = ctx.createGain();
        osc2.connect(gain2);
        gain2.connect(ctx.destination);
        osc2.type = "triangle";
        osc2.frequency.setValueAtTime(330, now);
        osc2.frequency.setValueAtTime(440, now + 0.18);
        osc2.frequency.setValueAtTime(554, now + 0.36);
        osc2.frequency.setValueAtTime(659, now + 0.54);
        gain2.gain.setValueAtTime(0.001, now);
        gain2.gain.linearRampToValueAtTime(0.08, now + 0.08);
        gain2.gain.linearRampToValueAtTime(0.09, now + 0.3);
        gain2.gain.exponentialRampToValueAtTime(0.001, now + 1.1);
        osc2.start(now);
        osc2.stop(now + 1.15);
      } else if (kind === "success") {
        osc.type = "sine";
        osc.frequency.setValueAtTime(660, now);
        osc.frequency.setValueAtTime(880, now + 0.1);
        gain.gain.setValueAtTime(0.1, now);
        gain.gain.exponentialRampToValueAtTime(0.001, now + 0.25);
        osc.start(now);
        osc.stop(now + 0.27);
      } else {
        // tap
        osc.type = "sine";
        osc.frequency.setValueAtTime(1200, now);
        gain.gain.setValueAtTime(0.08, now);
        gain.gain.exponentialRampToValueAtTime(0.001, now + 0.06);
        osc.start(now);
        osc.stop(now + 0.07);
      }
    } catch {
      // Audio optional
    }
  }, [ensureContext]);

  return { unlockAudio, playTone };
}
